import { Link } from 'react-router-dom';
import { Car, Check, Clock, Shield, ArrowRight, Phone, Zap, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const BusinessVehicleFinance = () => {
  const vehicleTypes = [
    'Utes & Dual Cabs',
    'Vans & Delivery Vehicles',
    'Commercial Sedans',
    'Light Trucks',
    'Trailers',
    'Forklifts',
    'Buses & Coaches',
    'Specialised Vehicles'
  ];

  const industries = [
    'Trades & Services',
    'Delivery & Couriers',
    'Sales Teams',
    'Property Services',
    'Landscaping',
    'Cleaning Services',
    'Security',
    'Healthcare'
  ];

  const faqs = [
    {
      q: "What business vehicles can I finance?",
      a: "We finance all types of business vehicles including utes, vans, light trucks, sedans, SUVs, buses, and specialised vehicles. Both new and used vehicles are eligible, and we work with all major manufacturers."
    },
    {
      q: "How is business vehicle finance different from personal car loans?",
      a: "Business vehicle finance typically offers tax benefits that personal loans don't. You may be able to claim GST on the purchase, depreciation, and interest as business expenses. We also assess applications based on business income rather than just personal income."
    },
    {
      q: "Can I finance a fleet of vehicles?",
      a: "Absolutely. We specialise in fleet finance and can structure deals for multiple vehicles. Fleet finance often comes with better rates and streamlined administration. Contact us to discuss your fleet requirements."
    },
    {
      q: "What's a novated lease?",
      a: "A novated lease is a three-way agreement between you, your employer, and the finance company. Your employer makes lease payments from your pre-tax salary, which can provide significant tax savings. We can arrange novated leases for eligible employees."
    },
    {
      q: "How fast can I get approved?",
      a: "Many business vehicle finance applications are approved within 24 hours, and straightforward applications with complete documentation can be approved same day. Timing depends on the applicant, the lender's credit criteria and the type of loan."
    },
    {
      q: "Do I need a deposit?",
      a: "Not necessarily. We offer no-deposit options for many business vehicle finance applications. A deposit may secure better rates, but it's often not required."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Business Vehicle Finance | Commercial Vehicle Loan Broker"
        description="Business vehicle finance from Australia's trusted commercial vehicle loan broker. Finance utes, vans, trucks & fleet vehicles. Fast 24hr approvals. No deposit options. Call 1800 003 527."
        keywords="business vehicle finance, commercial vehicle finance, ute finance, van finance, fleet finance, business car loans, commercial vehicle loan broker, novated lease, work vehicle finance"
        canonical="/business-vehicle-finance"
      />

      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1449965408869-ebd3fee6aaa5?auto=format&fit=crop&w=1920&q=80)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-6">
                <Car size={18} className="text-gold-500" />
                <span className="text-gold-500 font-body text-sm font-medium">Commercial Vehicle Loan Broker</span>
              </div>
              
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight leading-none mb-6">
                Business Vehicle<br />
                <span className="gold-gradient-text">Finance</span>
              </h1>
              
              <p className="font-body text-lg text-white/70 mb-8 max-w-xl">
                Get your business vehicles on the road with Australia's trusted <strong className="text-white">commercial vehicle loan broker</strong>. Whether you need a single ute or an entire fleet, we'll find you competitive rates with fast approvals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href={PORTAL_LINKS.COMMERCIAL} data-testid="business-vehicle-finance-hero-primary-link">
                  <Button 
                    data-testid="vehicle-finance-apply-btn"
                    className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-heading font-bold text-lg uppercase tracking-wider px-8 py-6 hover:scale-105 transition-transform shadow-lg shadow-purple-500/30"
                  >
                    <Sparkles size={20} className="mr-2" />
                    Get Free Quote
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </a>
                <a href="tel:+611800003527">
                  <Button 
                    variant="outline"
                    className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
                  >
                    <Phone size={20} className="mr-2" />
                    1800 003 527
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-6 text-white/60">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-gold-500" />
                  <span className="font-body text-sm">24hr Approvals</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-gold-500" />
                  <span className="font-body text-sm">Australian Owned</span>
                </div>
              </div>
            </div>

            <div className="bg-navy-800/80 backdrop-blur border border-white/10 p-8">
              <h3 className="font-heading font-bold text-2xl text-white uppercase mb-6">Quick Quote</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Loan Amounts</span>
                  <span className="font-mono text-xl text-gold-500">$15K - $500K+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Loan Terms</span>
                  <span className="font-mono text-xl text-white">12 - 84 months</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Approval Time</span>
                  <span className="font-mono text-xl text-gold-500 text-right">From 24 hours<span className="block font-body text-[10px] text-white/50">Depends on applicant, credit criteria &amp; loan type</span></span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-white/60">Deposit</span>
                  <span className="font-mono text-xl text-white">From $0</span>
                </div>
              </div>
              <Link to="/calculator" className="block mt-6">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                  Calculate Repayments
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Business Vehicle Finance */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">
                What is Business Vehicle Finance?
              </h2>
              <div className="space-y-4 font-body text-white/70">
                <p>
                  Business vehicle finance helps Australian companies acquire work vehicles without the large upfront cost. From tradies needing a new ute to corporations managing fleet vehicles, we provide tailored finance solutions.
                </p>
                <p>
                  As your <strong className="text-white">commercial vehicle loan broker</strong>, Flash Funding compares options from over 40 lenders to find you the best deal. We handle the paperwork, negotiate rates, and get you approved fast.
                </p>
                <p>
                  Our business vehicle finance options include chattel mortgage, finance lease, operating lease, novated lease, and commercial hire purchase — giving you flexibility in how you structure the finance.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {vehicleTypes.map((type, index) => (
                <div key={index} className="flex items-start gap-3 bg-navy-700 p-4">
                  <Check size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                  <span className="font-body text-white/80 text-sm">{type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4">
              Industries We Serve
            </h2>
            <p className="font-body text-white/60 max-w-2xl mx-auto">
              Business vehicle finance for all industries across Australia
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <div key={index} className="bg-navy-800 border border-white/5 p-6 text-center hover:border-gold-500/30 transition-colors">
                <span className="font-heading font-bold text-white uppercase">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-4">
              <HelpCircle size={16} className="text-gold-500" />
              <span className="text-gold-500 font-body text-sm font-medium">Common Questions</span>
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight">
              Business Vehicle Finance FAQ
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="bg-navy-700 border border-white/5 px-6"
              >
                <AccordionTrigger className="text-left font-heading font-bold text-lg text-white hover:text-gold-500 py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-white/70 pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gold-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-black uppercase tracking-tight mb-6">
            Ready for Business Vehicle Finance?
          </h2>
          <p className="font-body text-black/70 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote. Our Australian team will find you the best vehicle finance deal for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="business-vehicle-finance-final-primary-link">
              <Button 
                className="bg-navy-900 text-white hover:bg-navy-800 font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
                data-testid="business-vehicle-finance-final-primary-cta"
              >
                <Sparkles size={20} className="mr-2" />
                Get Free Quote
              </Button>
            </a>
            <a href="tel:+611800003527">
              <Button 
                variant="outline"
                className="border-black text-black hover:bg-black hover:text-gold-500 font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
              >
                Call 1800 003 527
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessVehicleFinance;
